import React, { useState, useEffect, useRef } from 'react';
import { FaFilter, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import { fetchCategories } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

// Utility function to format camelCase text with spaces
const formatCategoryName = (text) => {
  if (!text) return '';
  
  // Add space before capital letters, but not at the beginning
  return text.replace(/([A-Z])/g, ' $1')
    // Handle special characters like 'ä', 'ö', 'ü'
    .replace(/([äöüÄÖÜ])/g, ' $1')
    // Clean up multiple spaces and trim
    .replace(/\s+/g, ' ')
    .trim();
};

// Custom Searchable Dropdown Component
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  label,
  searchPlaceholder
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input at the top of dropdown */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder || t('filters.category.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none ${
                    option.value === value ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('filters.category.noOptionsFound')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryFilter = ({ onCategorySelect, onClear }) => {
  const { t, language } = useLanguage();

  // Localized display name for a category/subcategory node. Prefers the
  // localized `labels` map (de/fr/it/en, populated by apply_category_labels.js),
  // then the legacy German name, then the stable key as a last resort. Safe
  // before labels exist: it simply falls back to German.
  const pickName = (node) =>
    node?.labels?.[language] || node?.labels?.de || node?.de || node?.en || '';
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategoriesData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setSelectedSubcategory('');
    if (categoryName) {
      const cat = categories.find(c => c.category.en === categoryName);
      onCategorySelect(categoryName, '', pickName(cat?.category), '');
    }
  };

  const handleSubcategoryChange = (subcategoryName) => {
    setSelectedSubcategory(subcategoryName);
    if (selectedCategory && subcategoryName) {
      const cat = categories.find(c => c.category.en === selectedCategory);
      const sub = cat?.subcategories?.find(s => s.en === subcategoryName);
      onCategorySelect(selectedCategory, subcategoryName, pickName(cat?.category), pickName(sub));
    }
  };

  const handleClear = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    onClear();
  };

  const selectedCategoryData = categories.find(cat => cat.category.en === selectedCategory);
  const subcategories = selectedCategoryData?.subcategories || [];

  // Prepare options for dropdowns
  const categoryOptions = categories.map(cat => ({
    value: cat.category.en,
    label: formatCategoryName(pickName(cat.category))
  }));

  // Value MUST be the English key (sub.en): the backend matches the composite
  // "<parentEn>_<subEn>" id, not the German display name. Subs without an en
  // key are unreachable (hidden on the storefront too), so drop them.
  const subcategoryOptions = subcategories
    .filter(sub => sub.en)
    .map(sub => ({
      value: sub.en,
      label: formatCategoryName(pickName(sub))
    }));

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        >
          <FaFilter className="text-gray-400" />
          {t('filters.category.title')}
          {isOpen ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </button>

        {(selectedCategory || selectedSubcategory) && (
          <button
            onClick={handleClear}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            {t('filters.category.clearFilter')}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-[99999]" style={{ transform: 'translateZ(0)' }}>
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('filters.category.modalTitle')}</h3>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-gray-500">{t('filters.category.loading')}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Category Dropdown with built-in search */}
                <SearchableDropdown
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder={t('filters.category.categoryPlaceholder')}
                  label={t('filters.category.categoryLabel')}
                  searchPlaceholder={t('filters.category.categorySearchPlaceholder')}
                />

                {/* Subcategory Dropdown with built-in search - Only show if category is selected */}
                {selectedCategory && (
                  <SearchableDropdown
                    options={subcategoryOptions}
                    value={selectedSubcategory}
                    onChange={handleSubcategoryChange}
                    placeholder={t('filters.category.subcategoryPlaceholder')}
                    label={t('filters.category.subcategoryLabel')}
                    searchPlaceholder={t('filters.category.subcategorySearchPlaceholder')}
                  />
                )}

                {/* Selected Filter Display */}
                {(selectedCategory || selectedSubcategory) && (
                  <div className="mt-3 p-2 bg-indigo-50 border border-indigo-200 rounded-md">
                    <p className="text-xs text-indigo-800">
                      <span className="font-medium">{t('filters.category.selected')}</span>
                      {selectedCategory && (
                        <span className="ml-1">
                          {formatCategoryName(pickName(categories.find(cat => cat.category.en === selectedCategory)?.category))}
                        </span>
                      )}
                      {selectedSubcategory && (
                        <span className="ml-1">
                          {' > '}{formatCategoryName(pickName(subcategories.find(s => s.en === selectedSubcategory)) || selectedSubcategory)}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryFilter; 